import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpService } from 'src/app/Core/interceptors/http/http.service';
import { environment } from 'src/environments/environment';
import { AuctionApiResponse, AuctionApiItem } from '../models/auction-api.model';
import { AuctionEvent } from '../models/auction-event.model';
import { TimeZoneService } from 'src/app/Shared/services/time-zone/time-zone.service';

@Injectable({
  providedIn: 'root'
})
export class AuctionsCalendarApiService {
  private apiUrl = `${environment.url}/main-website-general/auctionsCalendar`;

  constructor(
    private http: HttpService,
    private timezoneService: TimeZoneService
  ) {}

  /**
   * Get all auctions from the API
   * The HttpService automatically handles language headers
   */
  getAuctionsCalendar(): Observable<AuctionEvent[]> {
    return this.http.get(this.apiUrl).pipe(
      map((response: AuctionApiResponse) => {
        if (response.status === 'success' && response.data && response.data.list) {
          return this.transformAuctionsData(response.data.list);
        }
        throw new Error('Invalid API response format');
      }),
      catchError((error) => {
        console.error('Error fetching auctions calendar:', error);
        const errorMessage = this.getErrorMessage(error);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Transform API auction items to AuctionEvent format
   */
  private transformAuctionsData(apiItems: AuctionApiItem[]): AuctionEvent[] {
    return apiItems.map(item => this.transformAuctionItem(item));
  }

  /**
   * Transform a single API auction item to AuctionEvent
   */
  private transformAuctionItem(item: AuctionApiItem): AuctionEvent {
    return {
      id: item.id,
      title: item.title,
      date: this.parseDate(item.date),
      time: item.time,
      location: item.location,
      description: item.description,
      status: item.status,
      auctionUrl: item.auction_url,
      consignor: item.consignor,
      email: item.email,
      phone: item.phone,
      previewDateStart: item.preview_date_start,
      previewDateEnd: item.preview_date_end,
      startDate: item.start_date,
      endDate: item.end_date,
      previewDate: this.formatPreviewDate(item.preview_date_start, item.preview_date_end),
      isInterested: item.is_interested
    };
  }

  /**
   * Parse date string to Date object
   */
  private parseDate(dateString: string): Date {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateString}`);
      return new Date();
    }
    return date;
  }

  /**
   * Format preview date range for display (backward compatibility)
   */
  private formatPreviewDate(startDate: string | null, endDate: string | null): string {
    if (!startDate && !endDate) return '';
    if (startDate && !endDate) return this.formatSingleDate(startDate);
    if (!startDate && endDate) return this.formatSingleDate(endDate);
    return `${this.formatSingleDate(startDate!)} - ${this.formatSingleDate(endDate!)}`;
  }

  /**
   * Format a single date string for display using user's timezone
   */
  private formatSingleDate(dateString: string): string {
    try {
      return this.timezoneService.formatDateToLocale(dateString, 'en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }) || dateString;
    } catch (error) {
      return dateString;
    }
  }

  /**
   * Get user-friendly error message
   */
  private getErrorMessage(error: any): string {
    if (error.status === 0) {
      return 'Unable to connect to the server. Please check your internet connection.';
    }
    if (error.status === 404) {
      return 'Auction calendar data not found.';
    }
    if (error.status === 500) {
      return 'Server error occurred. Please try again later.';
    }
    if (error.error && error.error.message) {
      return error.error.message;
    }
    return 'An unexpected error occurred while loading auctions. Please try again.';
  }

  /**
   * Get available years from auctions data
   */
  getAvailableYears(): number[] {
    const currentYear = new Date().getFullYear();
    return [
      currentYear - 1,
      currentYear,
      currentYear + 1,
      currentYear + 2
    ];
  }
}
